import { useLoginByAuto } from "@/api/clientApi/login";
import { DialogType } from "@/components/Dialogs/types";
import { useDialog } from "@/components/Dialogs/useDialog";
import {
  LocalStorageKey,
  MERGE_TIME,
  OLD_APP_NAME,
} from "@/constants/constants";
import { useActivity } from "@/hooks/useActivity";
import { useRepeatActivityContainBasic } from "@/hooks/useRepeatActivityContainBasic.ts";
import { useI18n } from "@/i18n";
import { getLocalStorageItem, setLocalStorageItem } from "@/lib/localStorage";
import { sdkManager } from "@/lib/sdk/sdkManager";
import { ThinkingDataSDK } from "@/lib/sdk/thinkingData";
import { SDKName } from "@/lib/sdk/types";
import { requestSaveSubscription } from "@/lib/subscribe";
import { getActivityCfg } from "@/stores/activityStore.ts";
import {
  getShareTextConfig,
  getUserShareUrl,
} from "@/stores/affiliateStore.ts";
import { appName } from "@/stores/configStore";
import { getDeviceObject } from "@/stores/deviceStore";
import {
  getAgentRewardDotData,
  getUserInfo,
  userStore,
} from "@/stores/userStore";
import { reconnectWS, reportEvent } from "@/stores/websocketStore";
import { ActivityTypeEnum } from "@/types/compontent/base/baseEnum.ts";

import { useBounsCenterInit } from "../bonusCenter/useBounsCenterInit";
import { useAutoLogin } from "../useAutoLogin";

export const useAfterLogin = () => {
  const { initData } = useBounsCenterInit();
  const { isAutoLogin, bChangeToken } = useAutoLogin();
  const { showDialog } = useDialog();
  const { getAllRepeatCfg } = useActivity();
  const { t } = useI18n();
  const showMergeTips = () => {
    if (typeof window !== "undefined") {
      const oldAppId = getLocalStorageItem(LocalStorageKey.MergeAppId, 0);
      const oldAppIdConfirm = getLocalStorageItem(
        LocalStorageKey.MergeAppIdConfirm,
        0,
      );
      if (oldAppId && oldAppId != oldAppIdConfirm) {
        showDialog({
          type: DialogType.Tips,
          zIndex: 9999,
          data: {
            content: (
              <p class="text-general-light">
                {t("newapp.rules", {
                  oldApp: OLD_APP_NAME,
                  newApp: appName(),
                  Date: MERGE_TIME,
                })}
              </p>
            ),

            closeOnBackdrop: false,
            title: t("common.tips"),
            confirmText: t("common.confirm"),
            onConfirm: () => {
              setLocalStorageItem(LocalStorageKey.MergeAppIdConfirm, oldAppId);
            },
            onClose: () => {
              setLocalStorageItem(LocalStorageKey.MergeAppIdConfirm, oldAppId);
            },
          },
        });
      }
    }
  };
  const dealAfterLogin = () => {
    let isReqAutoLogin = false;
    const lastLoginTime = getLocalStorageItem(LocalStorageKey.LastLoginTime, 0);
    const lastLoginDate = new Date(lastLoginTime);
    const now = new Date();
    const nowTime = now.getTime();
    setLocalStorageItem(LocalStorageKey.LastLoginTime, nowTime);

    if (now.getDate() !== lastLoginDate.getDate()) {
      // 每天第一次登录
      if (isAutoLogin()) {
        // 隔天请求自动登录，刷新token有效期，也会有心跳返回登录事件
        isReqAutoLogin = true;
      }
    }

    if (isAutoLogin()) {
      const clientDevice = getLocalStorageItem(
        LocalStorageKey.ClientDevice,
        {},
      );
      const deviceNow = getDeviceObject();
      if (JSON.stringify(clientDevice) != JSON.stringify(deviceNow)) {
        isReqAutoLogin = true;
      }
    }

    if (isReqAutoLogin) {
      const uid = userStore.userInfo?.uid;
      const token = userStore.token;
      const hasChangedToken = bChangeToken();
      if (uid && token) {
        useLoginByAuto({
          uid: uid,
          token: token,
          new_login: hasChangedToken,
        });
      }
      // TODO: 处理自动登录请求
    } else {
      if (isAutoLogin()) {
        // 自动登录
        const lastLoginEventTime = getLocalStorageItem(
          LocalStorageKey.LastLoginEventTime,
          0,
        );
        const timeGap = nowTime - lastLoginEventTime;
        if (timeGap > 30 * 60 * 1000) {
          // 30分钟间隔
          setLocalStorageItem(LocalStorageKey.LastLoginEventTime, nowTime);
          // TODO: 发送登录事件
          // googleEvent({ sendType: 0, event: "login", event_type: "af_login" });
        }
      }
    }
  };

  const afterLoginSuccess = () => {
    setTimeout(async () => {
      try {
        if (!document.body || !document.head) return;
        showMergeTips();
        reconnectWS();
        initData();
        dealAfterLogin();
        reportEvent();
        getAllRepeatCfg(); // 获取优惠活动
        sdkManager.reportHjUserProperties();
        await getUserInfo(true);
        sdkManager
          .getSDK<ThinkingDataSDK>(SDKName.ThinkingDataSDK)
          ?.login(userStore.userInfo?.uid?.toString() ?? "");
        // 登录成功后获取联盟计划奖励红点
        getAgentRewardDotData();
        requestSaveSubscription();

        getUserShareUrl();
        getShareTextConfig();

        const webToFirstDepositAppCfg = getActivityCfg(
          ActivityTypeEnum.appdownload,
        )?.cfg;
        if (
          webToFirstDepositAppCfg?.pop_ups_type === 3 &&
          userStore.userInfo?.is_pay
        ) {
          useRepeatActivityContainBasic().getWebToAppActivityAward();
        }
      } catch (error) {
        console.error("Error in afterLoginSuccess:", error);
      }
    });
  };

  return { afterLoginSuccess };
};
